import { Container, Row } from '@everlywell/leaves';
import { DisclaimerDataProps } from '~/components/PrePurchaseDisclaimer';
import { ScreenerDataProps } from '~/components/PrePurchaseScreener';
import React, { useContext, useEffect, useState } from 'react';
import { Promo } from 'utils/helpers/activePromoHelpers';
import { useMembershipProducts } from 'utils/hooks';
import { CreditData } from 'utils/hooks/useMembershipProducts';
import { UserContext } from 'utils/hooks/useUserContext/context';
import { ProductDataType, ProductsBySlugType } from 'utils/types';

import {
  usePrePurchaseModalContext,
  PrePurchaseModalActionsTypes,
} from '../../../contexts/PrePurchaseModal';
import { filterPromosForMemberships } from '../../../utils/helpers/membershipHelpers';
import PrePurchaseWrapper from '../../PrePurchaseWrapper';
import { ProductCard } from './ProductCard';
import { PromoCard, PromoProps } from './PromoCard';
import * as S from './styles';

export type ProductDisplayGridProps = {
  products: ProductDataType[];
  visiblePromos?: PromoProps[];
  activePromo?: Promo;
};

export const ProductDisplayGrid: React.FC<ProductDisplayGridProps> = ({
  products,
  visiblePromos,
  activePromo,
}) => {
  const { userData } = useContext(UserContext);
  const productsWithMembership = useMembershipProducts(
    userData,
    products as unknown as Record<number, ProductsBySlugType[string]>,
  ) as unknown as (ProductDataType & CreditData)[];
  const [sortedProducts, setSortedProducts] = useState<Array<ProductDataType>>(
    [],
  );
  const [promos, setPromos] = useState<PromoProps[] | null>(null);

  useEffect(() => {
    if (visiblePromos && visiblePromos.length > 0) {
      setPromos(
        visiblePromos.filter((promo) =>
          filterPromosForMemberships(
            promo.active,
            promo.slug,
            userData.membershipState,
          ),
        ),
      );
    }
  }, [userData.membershipState, visiblePromos]);

  useEffect(() => {
    productsWithMembership.length > 0 &&
      setSortedProducts(productsWithMembership);
  }, [productsWithMembership]);

  const prePurchaseModalContext = usePrePurchaseModalContext();

  const openScreenerFlow = (
    screener: ScreenerDataProps,
    disclaimer: DisclaimerDataProps,
    callback: () => void,
  ) => {
    prePurchaseModalContext?.dispatch({
      type: PrePurchaseModalActionsTypes.SetPrePurchaseScreener,
      payload: {
        isPrePurchaseScreenerOpen: true,
        prePurchaseScreenerData: screener,
        prePurchaseDisclaimerData: disclaimer,
        elegibleCallback: callback,
      },
    });
  };

  const openDisclaimerFlow = (disclaimer: any) => {
    prePurchaseModalContext?.dispatch({
      type: PrePurchaseModalActionsTypes.SetPrePurchaseDisclaimer,
      payload: {
        isPrePurchaseDisclaimerOpen: true,
        prePurchaseDisclaimerData: disclaimer,
      },
    });
  };

  const handleOnScreenerFlow = (
    screener: any,
    disclaimer: any,
    callback: () => void,
  ) => {
    const hasScreener = Boolean(screener && disclaimer);
    const hasDisclaimer = Boolean(disclaimer);

    if (hasScreener) {
      openScreenerFlow(screener, disclaimer, callback);
    } else if (hasDisclaimer) {
      openDisclaimerFlow(disclaimer);
    } else {
      callback();
    }
  };

  return (
    <div
      role="region"
      id="productGrid"
      data-testid="productGrid"
      aria-live="polite"
    >
      <Container>
        <Row>
          <S.ProductGridCol xs={12}>
            {promos?.map((promo, index) => (
              <PromoCard key={index} {...promo} />
            ))}
            {sortedProducts.map((product: ProductDataType) => {
              if (!product) return null;

              return (
                <ProductCard
                  product={product}
                  handleOnScreenerFlow={handleOnScreenerFlow}
                  activePromo={activePromo}
                  key={product.sku}
                />
              );
            })}
          </S.ProductGridCol>
        </Row>
      </Container>
      <PrePurchaseWrapper />
    </div>
  );
};

export default ProductDisplayGrid;
